/**
 * Admin Context - Phase 3 Compliant
 * 
 * Uses Bearer token authentication exclusively.
 * X-Admin-ID header has been REMOVED.
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AdminContext = createContext(null);
const ADMIN_ROLES = new Set(['admin', 'moderator', 'support', 'superadmin']);

function isAdminRole(role) {
  return ADMIN_ROLES.has((role || '').toLowerCase());
}

function clearAdminStorage() {
  localStorage.removeItem('adminSessionId');
  localStorage.removeItem('adminUser');
  localStorage.removeItem('adminId');
}

function clearNonAdminStorage() {
  localStorage.removeItem('sessionId');
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');
  localStorage.removeItem('clubsClientToken');
  localStorage.removeItem('clubsClientUser');
  localStorage.removeItem('client_session_id');
  localStorage.removeItem('client_token');
  localStorage.removeItem('vendor_token');
  localStorage.removeItem('vendorToken');
}

export function AdminProvider({ children }) {
  const [adminUser, setAdminUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize from localStorage
  useEffect(() => {
    const sessionId = localStorage.getItem('adminSessionId');
    const storedUser = localStorage.getItem('adminUser');
    
    if (sessionId && storedUser) {
      try {
        const user = JSON.parse(storedUser);
        if (isAdminRole(user?.role)) {
          setAdminUser(user);
          setIsAuthenticated(true);
        } else {
          clearAdminStorage();
          setIsAuthenticated(false);
        }
      } catch (e) {
        // Invalid stored data, clear it
        clearAdminStorage();
      }
    } else {
      // Fallback: a user authenticated via the regular flow (clubs / unified
      // /login) stores its session under `sessionId` + `user`. If that user
      // has an admin/staff role, mirror it into the admin keys so the
      // AdminRouteGuard recognizes them. Without this mirror, /superadmin
      // bounces logged-in admins back to /login.
      const regularSession = localStorage.getItem('sessionId');
      const regularUserRaw = localStorage.getItem('user');
      if (regularSession && regularUserRaw) {
        try {
          const regularUser = JSON.parse(regularUserRaw);
          if (regularUser && isAdminRole(regularUser.role)) {
            localStorage.setItem('adminSessionId', regularSession);
            localStorage.setItem('adminUser', JSON.stringify(regularUser));
            localStorage.setItem('adminId', regularUser.id);
            setAdminUser(regularUser);
            setIsAuthenticated(true);
          }
        } catch (e) {
          // ignore; fall through to unauthenticated state
        }
      }
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const syncAdminAuth = (event) => {
      const sessionId = event.detail?.sessionId || localStorage.getItem('adminSessionId');
      const user = event.detail?.user;
      if (!sessionId || !user || !isAdminRole(user.role)) return;
      setAdminUser(user);
      setIsAuthenticated(true);
    };
    const clearAdminAuthState = () => {
      setAdminUser(null);
      setIsAuthenticated(false);
    };

    window.addEventListener('admin-auth-changed', syncAdminAuth);
    window.addEventListener('admin-auth-cleared', clearAdminAuthState);
    return () => {
      window.removeEventListener('admin-auth-changed', syncAdminAuth);
      window.removeEventListener('admin-auth-cleared', clearAdminAuthState);
    };
  }, []);

  // Login function - stores Bearer token
  const login = useCallback(async (username, password) => {
    try {
      // API expects 'identifier' field (can be email, username, or phone)
      const axiosResponse = await api.post('/auth/login', { identifier: username, password });
      const response = axiosResponse.data || axiosResponse;
      
      if (response.session_id && response.user) {
        // Check if user has admin or staff role
        if (!isAdminRole(response.user.role)) {
          throw new Error('Access denied: Admin or staff privileges required');
        }
        
        clearNonAdminStorage();

        // Store session token for Bearer auth
        localStorage.setItem('adminSessionId', response.session_id);
        localStorage.setItem('adminUser', JSON.stringify(response.user));
        // Legacy: Also store adminId for backward compat with some components
        localStorage.setItem('adminId', response.user.id);
        
        setAdminUser(response.user);
        setIsAuthenticated(true);
        
        return { success: true, user: response.user };
      }
      
      throw new Error('Invalid response from server');
    } catch (error) {
      const message = error.serverMessage || 
                     error.response?.data?.detail || 
                     error.response?.data?.message ||
                     error.message ||
                     'Login failed';
      return { success: false, error: message };
    }
  }, []);

  // Logout function - clears all tokens
  const logout = useCallback(() => {
    // Clear all auth data
    clearAdminStorage();
    
    // Also try to invalidate session on server (fire and forget)
    api.post('/auth/logout').catch(() => {});
    
    setAdminUser(null);
    setIsAuthenticated(false);
  }, []);

  // Verify session is still valid (works for both master admin + vendor admin)
  const verifySession = useCallback(async () => {
    const sessionId = localStorage.getItem('adminSessionId');
    if (!sessionId) {
      setIsAuthenticated(false);
      return false;
    }
    
    try {
      // Use /admin/me — accepts both admin sessions AND vendor sessions (Phase 1)
      const axiosResponse = await api.get('/admin/me');
      const response = axiosResponse.data || axiosResponse;
      const profile = response.data || response;
      if (profile && profile.id) {
        const user = {
          id: profile.id,
          username: profile.username,
          email: profile.email,
          role: profile.role,
          vendor_id: profile.vendor_id,
          is_vendor: profile.is_vendor,
        };
        setAdminUser(user);
        localStorage.setItem('adminUser', JSON.stringify(user));
        return true;
      }
      return false;
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
      }
      return false;
    }
  }, [logout]);

  // Get Bearer token for external use
  const getToken = useCallback(() => {
    return localStorage.getItem('adminSessionId');
  }, []);

  const value = {
    adminUser,
    isLoading,
    isAuthenticated,
    login,
    logout,
    verifySession,
    getToken,
    // Expose admin ID for components that need it
    adminId: adminUser?.id || localStorage.getItem('adminId'),
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
}

export default AdminProvider;
